import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import usePageMeta from '../hooks/usePageMeta';
import { trackEvent } from '../analytics';
import { startSubscriptionCheckout } from '../pricing/checkout';
import useBillingSummary from '../hooks/useBillingSummary';
import { isSubscriber as isSubscriberStatus } from '../pricing/subscription';
import {
  PRICING_TIERS,
  BUSINESS_TIER,
  FOUNDING_LAPSE_GRACE_DAYS,
} from '../pricing/plans';
import { TOP_UPS, topUpLabel } from '../pricing/topups';
import './PricingPage.css';

const FAQ = [
  {
    q: 'What does “founding” pricing mean?',
    a: `Founding rates are the launch rates. Lock one in and it stays fixed for as long as your subscription stays active — even if new-customer prices rise later. If you cancel and stay lapsed past ${FOUNDING_LAPSE_GRACE_DAYS} days, re-subscribing uses the current public rate. No countdown timers, no fake scarcity — just an honest reward for backing us early.`,
  },
  {
    q: 'Is 4K locked to a paid plan?',
    a: 'No. Every plan, including Free, can export up to 4K. The only difference on Free is a small Tweedia watermark on your renders — resolution is never capped. Subscribe to Solo or Pro to remove the watermark.',
  },
  {
    q: 'How does storage work?',
    a: 'Storage is pooled across your whole account, not capped per project. Your plan’s allowance (2 GB Free, 50 GB Solo, 250 GB Pro) covers every project you run, and you can keep as many projects as you like.',
  },
  {
    q: 'What are credits for?',
    a: 'Credits pay for rendering finished videos — larger jobs use more. Paid plans include a monthly allowance (75/month on Solo, 250/month on Pro) that refreshes each billing cycle and does not roll over. Need more in a busy month? Subscribers can top up any time.',
  },
  {
    q: 'What happens to my photos if I cancel?',
    a: 'While you’re subscribed, your raw photos stay in cloud storage. On the Free plan, raw photos are cleared 30 days after a project’s last activity — the finished videos you’ve rendered are kept. Always keep your own copies of anything irreplaceable.',
  },
  {
    q: 'Can I change plans later?',
    a: 'Yes. Upgrade, downgrade, or cancel any time from your account’s billing portal. Downgrades take effect at the end of the current billing period.',
  },
];

function buildJsonLd() {
  const url = 'https://tweediastudio.com/pricing';
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };
}

function PricingPage({ user, signIn }) {
  usePageMeta({
    title: 'Pricing — Founding Plans | Tweedia Studio',
    description:
      'Simple founding pricing: start free, or subscribe to Solo ($15/mo) or Pro ($39/mo) for pooled storage, monthly render credits, and watermark-free 4K exports.',
    canonicalPath: '/pricing',
    jsonLd: buildJsonLd(),
  });

  const [pendingPlan, setPendingPlan] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);

  // Without this the page renders a live subscribe button for the tier the
  // user already pays for — and the server's double-charge guard rejects it
  // with a 400, so the only action on the page is one that cannot succeed.
  // Plan changes belong in the billing portal, which lives on /account.
  //
  // The guard predicates on subscription.status, the same field the server
  // guards on, rather than the effective tier: a past_due account past its
  // grace window reads as tier `free` here while the callable still refuses
  // checkout, which is the exact dead end this is meant to remove.
  const { entitlements, subscription, loading: billingLoading } = useBillingSummary(user);
  const currentTierId = entitlements && entitlements.tier;
  const isSubscriber = !billingLoading && isSubscriberStatus(subscription);

  // Until that snapshot lands a subscriber is indistinguishable from a signed-out
  // visitor, so the paid cards fall through to live subscribe buttons and a click
  // inside the window reaches the same FAILED_PRECONDITION dead end. Treat
  // unresolved billing as not-yet-actionable. Signed-out visitors are unaffected:
  // the hook short-circuits with loading false and no listener, so for the people
  // who should see a live button, it stays live.
  const billingUnknown = Boolean(user) && billingLoading;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const startSubscription = async (tier) => {
    trackEvent('cta_click', { cta_id: `pricing_${tier.id}`, signed_in: !!user });
    if (!user) {
      signIn();
      return;
    }
    setCheckoutError(null);
    setPendingPlan(tier.id);
    // Shared with the #350 upsell prompts (pricing/checkout.js) so
    // `subscribe_checkout_started` is emitted from exactly one place. It also
    // owns the explicit cancel_url — without one an abandoned Stripe Checkout
    // would fall through to the server's success_url default and bounce to
    // /account, indistinguishable from success.
    const { error } = await startSubscriptionCheckout({
      planId: tier.planId,
      source: 'pricing_page',
      fromTier: currentTierId || 'free',
      successUrl: `${window.location.origin}/account`,
      cancelUrl: `${window.location.origin}/pricing`,
    });
    if (error) {
      setCheckoutError(error);
      setPendingPlan(null);
    }
  };

  const handleFreeCta = () => {
    trackEvent('cta_click', { cta_id: 'pricing_free', signed_in: !!user });
    if (!user) signIn();
  };

  return (
    <div className="pricing-page">
      <Header user={user} signIn={signIn} />
      <main className="pricing-main">
        <section className="pricing-hero">
          <h1>Simple, founding-member pricing</h1>
          <p className="pricing-hero-lede">
            Start free. Subscribe when you’re ready for pooled cloud storage,
            monthly render credits, and watermark-free 4K exports. Founding rates
            stay locked for as long as you stay subscribed.
          </p>
        </section>

        <section
          className="pricing-tiers"
          aria-label="Subscription plans"
          data-testid="pricing-tiers"
        >
          {PRICING_TIERS.map((tier) => (
            <div
              key={tier.id}
              className={`pricing-card${tier.featured ? ' pricing-card-featured' : ''}`}
              data-testid={`pricing-tier-${tier.id}`}
            >
              {tier.featured && <div className="pricing-card-badge">Most popular</div>}
              <div className="pricing-card-head">
                <h2 className="pricing-card-name">
                  {tier.name}
                  {tier.founding && (
                    <span className="pricing-founding-badge" data-testid={`founding-badge-${tier.id}`}>
                      Founding
                    </span>
                  )}
                </h2>
                <p className="pricing-card-tagline">{tier.tagline}</p>
                <div className="pricing-card-price">
                  <span className="pricing-card-amount">{tier.priceLabel}</span>
                  <span className="pricing-card-suffix">{tier.priceSuffix}</span>
                </div>
              </div>
              <ul className="pricing-card-features">
                {tier.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
              {/* Paid tiers only: the Free card's CTA ("start using it") stays
                  useful for a signed-in Free user, and entitlements read as
                  free when signed out. */}
              {tier.planId && tier.id === currentTierId ? (
                <span className="pricing-card-cta pricing-card-cta-current" data-testid={`pricing-cta-${tier.id}`}>
                  Your current plan
                </span>
              ) : tier.planId && isSubscriber ? (
                // Already subscribed to something else — checkout would be
                // rejected by the double-charge guard, so send them to the
                // portal, which is where up/downgrades actually happen.
                <Link
                  to="/account"
                  className="pricing-card-cta"
                  data-testid={`pricing-cta-${tier.id}`}
                >
                  Change plan
                </Link>
              ) : tier.planId ? (
                <button
                  type="button"
                  className="pricing-card-cta"
                  data-testid={`pricing-cta-${tier.id}`}
                  onClick={() => startSubscription(tier)}
                  disabled={billingUnknown || pendingPlan === tier.id}
                >
                  {pendingPlan === tier.id ? 'Starting…' : tier.ctaLabel}
                </button>
              ) : user ? (
                <Link
                  to="/studio"
                  className="pricing-card-cta"
                  data-testid={`pricing-cta-${tier.id}`}
                  onClick={handleFreeCta}
                >
                  {tier.ctaLabel}
                </Link>
              ) : (
                <button
                  type="button"
                  className="pricing-card-cta"
                  data-testid={`pricing-cta-${tier.id}`}
                  onClick={handleFreeCta}
                >
                  {tier.ctaLabel}
                </button>
              )}
            </div>
          ))}

          <div className="pricing-card pricing-card-business" data-testid="pricing-tier-business">
            <div className="pricing-card-head">
              <h2 className="pricing-card-name">{BUSINESS_TIER.name}</h2>
              <p className="pricing-card-tagline">{BUSINESS_TIER.tagline}</p>
              <div className="pricing-card-price">
                <span className="pricing-card-amount pricing-card-amount-contact">
                  {BUSINESS_TIER.priceLabel}
                </span>
              </div>
            </div>
            <ul className="pricing-card-features">
              {BUSINESS_TIER.features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
            <a
              className="pricing-card-cta pricing-card-cta-secondary"
              data-testid="pricing-cta-business"
              href={`mailto:${BUSINESS_TIER.contactEmail}?subject=Tweedia%20Business%20plan`}
              onClick={() => trackEvent('cta_click', { cta_id: 'pricing_business', signed_in: !!user })}
            >
              {BUSINESS_TIER.ctaLabel}
            </a>
          </div>
        </section>

        {checkoutError && (
          <p className="pricing-checkout-error" role="alert" data-testid="pricing-checkout-error">
            {checkoutError}
          </p>
        )}

        <p className="pricing-fine-print">
          Prices in USD. Founding rates stay locked while your subscription is
          active; if it lapses for more than {FOUNDING_LAPSE_GRACE_DAYS} days,
          re-subscribing uses the current public rate. Billing is web-only for
          now. See our <Link to="/terms">Terms</Link> for the full details.
        </p>

        {/* Top-ups are a secondary, subscriber-only purchase: a slim strip, not
            a second row of cards competing with the tiers above. Rendered from
            a static constant on purpose — /pricing is a marketing page that has
            to paint with no round-trip, so it does not call listCreditPacks(). */}
        <section
          className="pricing-topups"
          aria-labelledby="pricing-topups-heading"
          data-testid="pricing-topups"
        >
          <h2 id="pricing-topups-heading">Need more in a busy month?</h2>
          <p className="pricing-topups-sub">Top up credits any time on a paid plan.</p>
          <ul className="pricing-topups-list">
            {TOP_UPS.map((topUp) => (
              <li
                className="pricing-topup"
                key={topUp.id}
                data-testid={`pricing-topup-${topUp.id}`}
              >
                <span className="pricing-topup-amount">{topUpLabel(topUp.credits)}</span>
                <span className="pricing-topup-sep" aria-hidden="true"> — </span>
                <span className="pricing-topup-price">{topUp.priceLabel}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/credits"
            className="pricing-topups-cta"
            data-testid="pricing-topups-cta"
            onClick={() => trackEvent('cta_click', { cta_id: 'pricing_topups', signed_in: !!user })}
          >
            Buy top-ups →
          </Link>
        </section>

        <section className="pricing-faq" aria-labelledby="pricing-faq-heading">
          <h2 id="pricing-faq-heading">Pricing FAQ</h2>
          <div className="pricing-faq-list" data-testid="pricing-faq">
            {FAQ.map((item) => (
              <div className="pricing-faq-item" key={item.q}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default PricingPage;
